import React, {useContext, useEffect, useState} from 'react'
import useListChallengedScoredScenes from "./useListChallengedScoredScenes";
import {useStyles} from "./ChallengedScoredScenesSection.css";
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTablePrimary.css";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import {Edit, Info, KeyboardArrowDown, KeyboardArrowUp} from "@mui/icons-material";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {useIntl} from "react-intl";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import ScoredSceneSummarySlider from "../../videosScored/utils/sliders/ScoredSceneSummarySlider";
import {useHistory} from "react-router-dom";
import {Box, Checkbox, Collapse, FormControlLabel, IconButton} from "@mui/material";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import ChallengedDecisionsSection from "../challengedDecisionsOfScoredScene/ChallengedDecisionsSection";
import usePagination from "../../../../commons/utils/usePagination";
import TablePagination from "@mui/material/TablePagination";
import ScoredSceneCardBasic from "../../videosScored/card/scoredSceneCardBasic/ScoredSceneCardBasic";
import {VideoCutSlider} from "../../videosScored/overview/ScoredScenesSection.css";

const ChallengedScoredScenesSection = () => {
    const classes = useStyles();
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    const {
        challengedScoredScenes,
        loading,
        challengedDecisionsNumbers,
        updateChallengedDecisionsNumberOfScoredScenes
    } = useListChallengedScoredScenes()

    // Internationalization for simple strings
    const intl = useIntl();

    const history = useHistory();

    // Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    // Collapsible table state
    const [openIndex, setOpenIndex] = React.useState();

    // Pagination for the table handling
    const {page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} = usePagination()

    // Toggle for showing only scored scenes with at least one open challenged decision
    const [hideResolved, setHideResolved] = useState(true)

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: intl.formatMessage({
                id: 'app.challengedDecision.overview.appbar.header',
                defaultMessage: 'Challenged Decisions'
            }),
            description: intl.formatMessage({
                id: 'app.challengedDecision.overview.appbar.description',
                defaultMessage: 'Check feedback from the users...'
            }),
            actions: <FormControlLabel
                control={
                    <Checkbox value={hideResolved}
                              checked={hideResolved}
                              onChange={(event) => setHideResolved(event.target.checked)}/>}
                label={intl.formatMessage({
                    id: 'app.challengedDecision.overview.appbar.hideResolved',
                    defaultMessage: 'Hide resolved video scenes'
                })}/>
        })
    }, [hideResolved])

    const getChallengedScoredScenes = () => {

        // Return all scored scenes, independent of the resolution
        if (!hideResolved) {
            return challengedScoredScenes
        }

        // Only return scored scenes, which have unresolved challenged decisions
        return challengedScoredScenes.filter((scene) => challengedDecisionsNumbers?.find((v) => v._id === scene.key)?.count > 0)
    }

    // Show loading indication while loading the list of challenged decisions
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.challengedDecision.overview.challengedScoredScenes.loading',
                defaultMessage: 'Loading Challenged Video Scenes...'
            })}/>
    }

    return (
        <Paper className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell align={"center"} width={"5%"} key='collapsible'></StyledTableCell>
                            <StyledTableCell align={"center"} width={"10%"} key='numberOfUnresolvedChallengedDecisions'>
                                {intl.formatMessage({
                                    id: 'app.challengedDecision.overview.table.column.numberUnresolvedDecisions',
                                    defaultMessage: '# Open'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"40%"} key="video">
                                {intl.formatMessage({
                                    id: 'app.challengedDecision.overview.table.column.video',
                                    defaultMessage: 'Video'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"center"} width={"35%"} key='scoreSummary'>
                                {intl.formatMessage({
                                    id: 'app.challengedDecision.overview.table.column.scoreSummary',
                                    defaultMessage: 'Score Summary'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"right"} width={"10%"} key='actions'>
                                {intl.formatMessage({
                                    id: 'app.challengedDecision.overview.table.column.actions',
                                    defaultMessage: 'Actions'
                                })}
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {getChallengedScoredScenes().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((challengedScoredScene, index) => {
                            return (
                                <React.Fragment key={challengedScoredScene.key}>
                                    <StyledTableRow hover key={challengedScoredScene.key}>

                                        {/* Collapsible */}
                                        <StyledTableCell key="collapsible">
                                            {openIndex === index
                                                ? <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(null)}>
                                                    <KeyboardArrowUp/>
                                                </IconButton>
                                                : <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(index)}>
                                                    <KeyboardArrowDown/>
                                                </IconButton>}
                                        </StyledTableCell>

                                        {/* Number of challenged decisions of the scored scene */}
                                        <StyledTableCell key="numberOfUnresolvedChallengedDecisions" align={"center"}>
                                            <div className={classes.numberOfChallengedDecisions}>
                                                {challengedDecisionsNumbers?.find((v) => v._id === challengedScoredScene.key)?.count || '*'}
                                            </div>
                                        </StyledTableCell>

                                        {/* Scored scene (enriched with with scored scene range on top) */}
                                        <StyledTableCell key="video">
                                            <ScoredSceneCardBasic scoredScene={challengedScoredScene}
                                                                  enrichedComponentTop={<div
                                                                      className={classes.horizontalSpacedContainer}>
                                                                      <VideoCutSlider
                                                                          value={[challengedScoredScene.basics.sceneStart, challengedScoredScene.basics.sceneEnd]}
                                                                          valueLabelDisplay="off"
                                                                          min={0}
                                                                          max={challengedScoredScene.video.vimeo.duration}
                                                                      />
                                                                  </div>}/>
                                        </StyledTableCell>

                                        {/* Score summary slider */}
                                        <StyledTableCell key='scoreSummary'>
                                            <div className={classes.scoreSummaryRow}>
                                                <ScoredSceneSummarySlider scoredScene={challengedScoredScene}
                                                                          timeFormat="mm:ss:SS"/>
                                            </div>
                                        </StyledTableCell>

                                        {/* Actions */}
                                        <StyledTableCell align={"right"} key='action'>
                                            <IconButton
                                                onClick={() => history.push('/admin/videos/' + challengedScoredScene.video.key + '/scoredScenes/' + challengedScoredScene.key + '/edit')}
                                                size="large">
                                                {isOperationOnObjectAllowed(challengedScoredScene)
                                                    ? <Edit/>
                                                    : <Info/>}
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>

                                    {/* Collapsible Content - Challenged Decisions of the respective scored scene */}
                                    <StyledTableRow>
                                        <StyledTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={6}>
                                            <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                                                <Box margin={1} marginLeft={10}>
                                                    <ChallengedDecisionsSection scoredScene={challengedScoredScene}
                                                                                onStatusUpdate={updateChallengedDecisionsNumberOfScoredScenes}/>
                                                </Box>
                                            </Collapse>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination section */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={challengedScoredScenes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default ChallengedScoredScenesSection